import { scaleBand, scaleLinear } from 'd3-scale';
import { Box } from '@mentimeter/ragnar-ui/box';
import { shade } from '@mentimeter/ragnar-colors';
import { useState } from 'react';
import type { Visualization } from '../types';
import { SvgWithFont } from '../utils/SvgWithFont';
import { AxisBottom } from './AxisBottom';
import { getDisplayValues, getCorrectRangePositions } from './utils';
import { CorrectAnswerArea } from './CorrectAnswerArea';
import { BOTTOM_AXIS_HEIGHT, SVG_INNER_WIDTH_PADDING } from './constants';
import { CorrectAnswerBox } from './CorrectAnswerBox';
import { ResultsArea } from './ResultsArea';
import { Distribution } from './Distribution';

export type GuessTheNumberData = Record<number, number>;

interface GuessTheNumberProps
  extends Omit<
    Visualization<GuessTheNumberData, never>,
    'data' | 'staticOptions'
  > {
  rangeMin: number;
  rangeMax: number;
  data: GuessTheNumberData;
  responseRangeScale: number;
  correctAnswer: {
    correctValue: number;
    range:
      | {
          min: number;
          max: number;
        }
      | undefined;
  };
  shouldShowCorrectAnswer: boolean;
  backgroundColor: string;
}

const AREA_PADDING_BOTTOM = 8;
const CORRECT_ANSWER_BOX_HEIGHT = 100;
const DISTRIBUTION_CIRCLE_RADIUS_MIN = 15;
const DISTRIBUTION_CIRCLE_RADIUS_MAX = 25;

export const GuessTheNumber = ({
  width,
  height,
  rangeMin,
  rangeMax,
  responseRangeScale,
  data: rawData,
  fontFamily,
  correctAnswer,
  shouldShowCorrectAnswer,
  textColor,
  colors,
  backgroundColor,
  disableAnimations,
}: GuessTheNumberProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);

  const svgWidth = rootWidth;
  const chartWidth = Math.max(svgWidth - SVG_INNER_WIDTH_PADDING * 2, 0);

  const svgHeight = Math.max(rootHeight - CORRECT_ANSWER_BOX_HEIGHT, 0);
  const resultsAreaHeight = Math.max(svgHeight - BOTTOM_AXIS_HEIGHT, 0);

  const displayValues = getDisplayValues(
    rangeMin,
    rangeMax,
    responseRangeScale,
  );

  const maxScore = Math.max(...Object.values(rawData)) || 0;
  const hasData = Object.keys(rawData).length > 0;

  const areaYScale = scaleLinear()
    .domain([0, maxScore])
    .range([svgHeight, BOTTOM_AXIS_HEIGHT + AREA_PADDING_BOTTOM]);

  const rangeMaxWithoutScale = Math.ceil(
    (rangeMax - rangeMin) / responseRangeScale,
  );

  const xScaleWithoutRangeScale = scaleLinear()
    .domain([0, rangeMaxWithoutScale])
    .range([0, chartWidth]);

  const xScale = scaleLinear()
    .domain([rangeMin, rangeMax])
    .range([0, chartWidth]);

  const discreteScale = scaleBand()
    .domain(displayValues.map(String))
    .range([0, chartWidth]);

  const tickWidth = discreteScale.bandwidth();
  const distributionCircleRadius = Math.min(
    Math.max(tickWidth * 0.8, DISTRIBUTION_CIRCLE_RADIUS_MIN),
    DISTRIBUTION_CIRCLE_RADIUS_MAX,
  );

  const areaColor = shade(colors[0], backgroundColor, 0.5);

  const correctRangePositions = getCorrectRangePositions(
    xScale,
    correctAnswer.correctValue,
    correctAnswer.range,
    tickWidth,
    rangeMin,
    rangeMax,
  );

  if (rootWidth <= 0 || rootHeight <= 0) {
    return null;
  }

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      justifyContent="flex-end"
      aria-hidden
    >
      <SvgWithFont
        width={rootWidth}
        height={svgHeight}
        fontFamily={fontFamily}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        data-testid="guess-the-number-svg"
      >
        <g transform={`translate(${SVG_INNER_WIDTH_PADDING}, 0)`}>
          {hasData && (
            <ResultsArea
              fill={areaColor}
              correctAnswerColor={colors[0]!}
              scores={rawData}
              yScale={areaYScale}
              xScale={xScaleWithoutRangeScale}
              disableAnimations={disableAnimations}
              paddingBottom={BOTTOM_AXIS_HEIGHT + AREA_PADDING_BOTTOM}
            />
          )}
          <CorrectAnswerArea
            height={resultsAreaHeight}
            startX={correctRangePositions.startX}
            endX={correctRangePositions.endX}
            correctAnswerX={xScale(correctAnswer.correctValue)}
            shouldShow={shouldShowCorrectAnswer}
            disableAnimations={disableAnimations}
          />
          <g transform={`translate(0,${resultsAreaHeight})`}>
            <AxisBottom
              rangeMin={rangeMin}
              rangeMax={rangeMax}
              chartWidth={chartWidth}
              numberOfTicksTarget={4}
              lineColor={colors[0]}
              textColor={textColor}
              tickColor={textColor}
              scale={responseRangeScale}
              tickWidth={tickWidth}
              disableAnimations={disableAnimations}
              correctAnswer={correctAnswer.correctValue}
              correctRange={correctAnswer.range}
              shouldShowCorrectAnswer={shouldShowCorrectAnswer}
            />
          </g>
          <g
            transform={`translate(0,${resultsAreaHeight + AREA_PADDING_BOTTOM})`}
          >
            <Distribution
              fill={areaColor}
              outlineColor={backgroundColor}
              scores={rawData}
              xScale={xScaleWithoutRangeScale}
              radius={distributionCircleRadius}
              isVisible={isHovering}
              disableAnimations={disableAnimations}
            />
          </g>
        </g>
      </SvgWithFont>
      <CorrectAnswerBox
        shouldShow={shouldShowCorrectAnswer}
        correctAnswer={correctAnswer.correctValue}
        shouldShowCorrectAnswer={shouldShowCorrectAnswer}
        xScale={xScale}
        acceptedRange={correctAnswer.range}
        disableAnimations={disableAnimations}
      />
    </Box>
  );
};
